import { AnimatePresence } from 'framer-motion';
import type { PageExperienceFragment } from '@/features/experiences/fragments/PageExperience.generated';
import { Flex, MotionText, Text } from '@/ui';
import { getProspectiveUrl } from '../utils';
import { ActionButtons } from './ActionButtons';
import { CopyLinkModal } from './CopyLinkModal';
import { EditableInput } from './EditableInput';
import { useEditableLinkState } from './useEditableLinkState';

type EditableLinkProps = {
  experience: PageExperienceFragment;
  publishedSlug?: string;
  handleOnFocus?: (focused: boolean) => void;
};

export const EditableLink = ({
  experience,
  publishedSlug,
  handleOnFocus,
}: EditableLinkProps) => {
  const {
    isEditing,
    slug,
    isUpdated,
    isModalOpen,
    modalTrigger,
    error,
    errorMessage,
    debouncedIsPending,
    inputRef,
    handleOnChange,
    handleOnBlur,
    handleUpdateSlug,
    handleCancel,
    handleConfirm,
    handleFocusInput,
  } = useEditableLinkState(publishedSlug, experience.id);

  const fullUrl = getProspectiveUrl(publishedSlug, slug);

  return (
    <Flex flexDirection={'column'} gap="2">
      <Flex>
        <Text as="span" fontSize="sm">
          {fullUrl}
        </Text>
      </Flex>

      <Flex>
        <EditableInput
          debouncedIsPending={debouncedIsPending}
          error={!!error}
          inputRef={inputRef}
          isEditing={isEditing}
          slug={slug}
          onBlur={handleOnBlur}
          onChange={(e) => handleOnChange(e.target.value)}
          onFocus={() => handleOnFocus?.(true)}
        />
        <ActionButtons
          error={!!error}
          experience={experience}
          fullUrl={fullUrl}
          isEditing={isEditing}
          isPending={debouncedIsPending}
          onMouseDown={handleFocusInput}
          onUpdate={handleUpdateSlug}
        />
      </Flex>

      <AnimatePresence mode="popLayout">
        {errorMessage && !isUpdated && (
          <MotionText
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            fontWeight={'bold'}
            initial={{ opacity: 0 }}
          >
            <Text
              as="span"
              color={!isEditing ? 'gray.500' : error ? 'red.500' : 'green.500'}
              fontSize="sm"
            >
              {errorMessage}
            </Text>
          </MotionText>
        )}
      </AnimatePresence>

      <CopyLinkModal
        isOpen={isModalOpen}
        modalTrigger={modalTrigger}
        onClose={handleCancel}
        onConfirm={handleConfirm}
      />
    </Flex>
  );
};
